'use client';

/**
 * Read-only audit pass over the local blob store.
 *
 * Walks every entry in OPFS (`<storage-root>/blobs/<hash>`) and IDB
 * (`knitlib-blobs.blobs` keyed by hash), recomputes `sha256(bytes)`
 * for each, and compares against the storage key. Cross-walks the
 * referenced hashes from Dexie rows (`photo` + `colorPhoto` fields)
 * so we can also surface orphans (stored but unreferenced) and
 * missing-refs (referenced but absent locally).
 *
 * This exists to chase the **stale-keyed blob bug** Veronika hit on
 * 2026-04-29 when her Mac joined her sync room: 16 unique blob
 * fetches failed with `hash-mismatch` because at least one peer was
 * serving bytes that didn't actually hash to the requested key. The
 * audit identifies which device has the bad data so the eviction
 * repair pass (next session) can target it precisely.
 *
 * Strict read-only — never deletes, re-keys, or otherwise mutates
 * the blob store. Logs structured `blob-audit-*` events to
 * VictoriaLogs for off-device analysis.
 *
 * Bounded: at most 100 mismatches and 100 orphans are logged per
 * run to avoid log floods on devices with many stale entries; the
 * summary count is unbounded so we still know the true magnitude.
 */

import { db } from './db';
import { BLOB_REF_PREFIX, blobRefHash, isBlobRef } from './blobStore';
import { log } from './log';

// ---------- types ----------

export interface BlobAuditReport {
  total: number;
  verified: number;
  mismatched: number;
  orphaned: number;
  missingRefs: number;
  durationMs: number;
}

interface StoredBlob {
  hash: string;
  bytes: Uint8Array;
  backend: 'opfs' | 'idb';
}

const MAX_MISMATCH_LOGS = 100;
const MAX_ORPHAN_LOGS = 100;
const MAX_MISSING_REF_LOGS = 100;

// ---------- helpers ----------

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const buf = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buf).set(bytes);
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

function magicBytesHex(bytes: Uint8Array): string {
  // First 8 bytes hex — enough to identify common image formats
  // (FFD8FF = JPEG, 89504E47 = PNG, 47494638 = GIF, 52494646 = WEBP/RIFF).
  const slice = bytes.subarray(0, 8);
  return Array.from(slice).map(b => b.toString(16).padStart(2, '0')).join('');
}

// ---------- OPFS walk ----------

async function listOpfsBlobs(): Promise<StoredBlob[]> {
  if (typeof navigator === 'undefined') return [];
  const storage = navigator.storage as
    | { getDirectory?: () => Promise<FileSystemDirectoryHandle> }
    | undefined;
  if (!storage?.getDirectory) return [];

  let blobsDir: FileSystemDirectoryHandle;
  try {
    const root = await storage.getDirectory();
    blobsDir = await root.getDirectoryHandle('blobs');
  } catch {
    // No blobs dir yet — fresh install. Not an error.
    return [];
  }

  const out: StoredBlob[] = [];
  // Use `values()` explicitly to get an async iterator of handles
  // (the `[Symbol.asyncIterator]` default yields `[name, handle]`
  // entry tuples, which broke our walk in early testing).
  type FileHandle = { kind: 'file' | 'directory'; name: string; getFile: () => Promise<File> };
  const dir = blobsDir as unknown as { values: () => AsyncIterable<FileHandle> };
  for await (const handle of dir.values()) {
    if (handle.kind !== 'file') continue;
    try {
      const file = await handle.getFile();
      const bytes = new Uint8Array(await file.arrayBuffer());
      out.push({ hash: handle.name, bytes, backend: 'opfs' });
    } catch (e) {
      // Unreadable file (rare; OPFS can transient-fail under iOS pressure).
      // Best-effort — skip and continue.
      log.warn('app', 'blob-audit-read-failed', {
        backend: 'opfs',
        key: handle.name,
        error: String(e),
      });
    }
  }
  return out;
}

// ---------- IDB walk ----------

async function listIdbBlobs(skip: Set<string>): Promise<StoredBlob[]> {
  if (typeof indexedDB === 'undefined') return [];

  const out: StoredBlob[] = [];
  // Open the blob DB the same way blobStore does — match the schema
  // dance so we don't accidentally create a v0 empty store. We open
  // explicitly with version 1 + an upgrade handler that creates the
  // store, but if the store is missing on success (e.g. another tab
  // opened at v1 without our handler), we just abort cleanly rather
  // than bumping the version (we don't want to mutate state in an
  // audit pass).
  const db = await new Promise<IDBDatabase | null>((resolve) => {
    const req = indexedDB.open('knitlib-blobs', 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains('blobs')) {
        req.result.createObjectStore('blobs');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
  if (!db) return [];

  if (!db.objectStoreNames.contains('blobs')) {
    db.close();
    return [];
  }

  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction('blobs', 'readonly');
    const store = tx.objectStore('blobs');
    const cursorReq = store.openCursor();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (!cursor) {
        resolve();
        return;
      }
      const key = String(cursor.key);
      if (skip.has(key)) {
        // Already covered by OPFS — OPFS is authoritative when both
        // backends have the same hash (matches `getBlob` precedence).
        cursor.continue();
        return;
      }
      const value = cursor.value;
      let bytes: Uint8Array | null = null;
      if (value instanceof ArrayBuffer) {
        bytes = new Uint8Array(value);
      } else if (value instanceof Blob) {
        // Blob → bytes is async; defer extraction.
        const blob = value;
        blob.arrayBuffer().then(buf => {
          out.push({ hash: key, bytes: new Uint8Array(buf), backend: 'idb' });
          cursor.continue();
        }, () => cursor.continue());
        return;
      } else if (ArrayBuffer.isView(value)) {
        const view = value as ArrayBufferView;
        const copy = new Uint8Array(view.byteLength);
        copy.set(new Uint8Array(view.buffer as ArrayBuffer, view.byteOffset, view.byteLength));
        bytes = copy;
      }
      if (bytes) {
        out.push({ hash: key, bytes, backend: 'idb' });
      }
      cursor.continue();
    };
    cursorReq.onerror = () => reject(cursorReq.error);
  });

  db.close();
  return out;
}

// ---------- Dexie reference walk ----------

interface DexieRef {
  hash: string;
  table: string;
  field: string;
  rowId: number;
}

async function collectDexieRefs(): Promise<DexieRef[]> {
  const out: DexieRef[] = [];
  const push = (table: string, field: string, value: unknown, rowId: number | undefined) => {
    if (typeof value !== 'string') return;
    if (!isBlobRef(value)) return;
    if (rowId == null) return;
    out.push({ hash: blobRefHash(value), table, field, rowId });
  };

  for (const row of await db.projects.toArray()) push('projects', 'photo', row.photo, row.id);
  for (const row of await db.yarns.toArray()) {
    push('yarns', 'photo', row.photo, row.id);
    push('yarns', 'colorPhoto', row.colorPhoto, row.id);
  }
  for (const row of await db.inventory.toArray()) push('inventory', 'photo', row.photo, row.id);
  for (const row of await db.techniques.toArray()) push('techniques', 'photo', row.photo, row.id);
  for (const row of await db.inspirations.toArray()) push('inspirations', 'photo', row.photo, row.id);
  for (const row of await db.charts.toArray()) push('charts', 'gridRef', row.gridRef, row.id);

  return out;
}

// ---------- public entry ----------

/**
 * Run a read-only audit and emit `blob-audit-*` events to VL. Returns
 * a summary count for UI display. Safe to call repeatedly; nothing
 * is mutated.
 */
export async function runBlobAudit(): Promise<BlobAuditReport> {
  const startedAt = Date.now();
  const runId = startedAt;
  log.info('app', 'blob-audit-start', { runId });

  // 1. Walk both blob backends. OPFS is authoritative for any hash
  //    that appears in both — `getBlob` returns the OPFS copy first.
  const opfsBlobs = await listOpfsBlobs();
  const opfsHashes = new Set(opfsBlobs.map(b => b.hash));
  const idbBlobs = await listIdbBlobs(opfsHashes);
  const stored = [...opfsBlobs, ...idbBlobs];

  // 2. Verify each stored blob's bytes hash to its storage key.
  let verified = 0;
  let mismatched = 0;
  let mismatchLogged = 0;
  for (const b of stored) {
    const computed = await sha256Hex(b.bytes);
    if (computed === b.hash) {
      verified++;
      continue;
    }
    mismatched++;
    if (mismatchLogged < MAX_MISMATCH_LOGS) {
      log.info('app', 'blob-audit-mismatch', {
        runId,
        key: b.hash,
        computedHash: computed,
        byteLen: b.bytes.byteLength,
        magicBytes: magicBytesHex(b.bytes),
        backend: b.backend,
      });
      mismatchLogged++;
    }
  }
  if (mismatched > MAX_MISMATCH_LOGS) {
    log.warn('app', 'blob-audit-truncated', {
      runId,
      omittedMismatches: mismatched - MAX_MISMATCH_LOGS,
    });
  }

  // 3. Cross-walk Dexie refs to identify orphans + missing-refs.
  const storedHashes = new Set(stored.map(b => b.hash));
  const refs = await collectDexieRefs();
  const referencedHashes = new Set(refs.map(r => r.hash));

  const orphanHashes: string[] = [];
  for (const h of storedHashes) {
    if (!referencedHashes.has(h)) orphanHashes.push(h);
  }

  // For each orphan, log with byteLen + backend (look up from `stored`).
  let orphanLogged = 0;
  for (const h of orphanHashes) {
    if (orphanLogged >= MAX_ORPHAN_LOGS) break;
    const entry = stored.find(b => b.hash === h);
    log.info('app', 'blob-audit-orphan', {
      runId,
      hash: h,
      byteLen: entry?.bytes.byteLength ?? null,
      backend: entry?.backend ?? null,
    });
    orphanLogged++;
  }
  if (orphanHashes.length > MAX_ORPHAN_LOGS) {
    log.warn('app', 'blob-audit-truncated', {
      runId,
      omittedOrphans: orphanHashes.length - MAX_ORPHAN_LOGS,
    });
  }

  // Missing-refs: each ref that points to a hash we don't have.
  // Log every row, capped — these are the photos that won't render
  // on this device.
  let missingRefs = 0;
  let missingLogged = 0;
  for (const r of refs) {
    if (storedHashes.has(r.hash)) continue;
    missingRefs++;
    if (missingLogged < MAX_MISSING_REF_LOGS) {
      log.info('app', 'blob-audit-missing-ref', {
        runId,
        hash: r.hash,
        table: r.table,
        field: r.field,
        rowId: r.rowId,
      });
      missingLogged++;
    }
  }
  if (missingRefs > MAX_MISSING_REF_LOGS) {
    log.warn('app', 'blob-audit-truncated', {
      runId,
      omittedMissingRefs: missingRefs - MAX_MISSING_REF_LOGS,
    });
  }

  const durationMs = Date.now() - startedAt;
  const report: BlobAuditReport = {
    total: stored.length,
    verified,
    mismatched,
    orphaned: orphanHashes.length,
    missingRefs,
    durationMs,
  };
  log.info('app', 'blob-audit-done', { runId, ...report });
  return report;
}

// Re-export for the dev hook + tests.
export const __testOnly = { sha256Hex, magicBytesHex, BLOB_REF_PREFIX };
